import React from 'react';
import { Book, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { SEO, breadcrumbs } from '../components/SEO';

interface Product {
  id: string;
  title: string;
  description: string;
  price: string;
  category: 'book' | 'course' | 'app';
  image: string;
  features: string[];
  buyLink: string;
  featured?: boolean;
}

const products: Product[] = [
  {
    id: 'allegro-mastery',
    title: 'Allegro Mastery Guide',
    description: 'Complete guide to building a successful business on Allegro marketplace',
    price: '$49.99',
    category: 'book',
    image: 'https://images.pexels.com/photos/1005324/literature-book-open-pages-1005324.jpeg',
    features: [
      '200+ pages of detailed strategies',
      'Step-by-step setup guide',
      'Marketing techniques',
      'Pricing strategies',
      'Case studies'
    ],
    buyLink: 'https://cemates.gumroad.com/l/allegro-mastery',
    featured: true
  }
];

export const Store: React.FC = () => {
  useLanguage();
  const categories = Array.from(new Set(products.map(product => product.category)));

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'book':
        return <Book className="w-5 h-5" />;
      default:
        return <Book className="w-5 h-5" />;
    }
  };

  return (
    <div className="pt-24 pb-20">
      <SEO 
        title="Digital Store"
        description="Browse books, courses, and digital products by Hüseyin Cem Ateş. E-commerce guides, development courses, and more."
        canonical="/store"
        jsonLd={breadcrumbs.store}
      />
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-semibold mb-4">
            Digital Store
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Browse my collection of books, courses, and digital products
          </p>
        </div>

        {categories.map(category => (
          <div key={category} className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <span className="text-muted-foreground">{getCategoryIcon(category)}</span>
              <h2 className="text-xl font-medium capitalize">
                {category}s
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products
                .filter(product => product.category === category)
                .map(product => (
                  <div
                    key={product.id}
                    className="bg-card rounded-lg border border-border overflow-hidden hover:border-foreground/20 transition-colors"
                  >
                    <div className="aspect-video w-full overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="p-5">
                      <h3 className="font-medium mb-2">{product.title}</h3>
                      <p className="text-muted-foreground text-sm mb-4 leading-relaxed">{product.description}</p>

                      <p className="text-lg font-semibold mb-4">
                        {product.price}
                      </p>

                      <ul className="mb-6 space-y-2">
                        {product.features.slice(0, 3).map((feature, index) => (
                          <li key={index} className="flex items-center text-muted-foreground text-sm">
                            <Star className="w-3 h-3 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <a
                        href={product.buyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center bg-foreground text-background py-2.5 rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
                      >
                        Buy Now
                      </a>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
